import { useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Brain,
  Copy,
  Edit3,
  Eye,
  Layers3,
  Plus,
  RefreshCw,
  Save,
  Search,
  Settings,
  Sparkles,
  Trash2,
  Wand2
} from "lucide-react";
import { generatePracticeManual, getGeminiStatus, recommendLanguage, recommendPracticeModules } from "./services/api";
import {
  createBlankManualModule,
  createManualRevision,
  duplicateManualModule,
  loadManualModules,
  restoreManualSnapshot,
  saveManualModules
} from "./services/storage";
import type {
  LanguageManualModule,
  LanguagePatternFormula,
  LanguageRecommendation,
  ManualDomain,
  ManualModule,
  ManualQuery,
  GeminiStatus,
  PracticeManual,
  PracticeManualModule,
  PracticeMatch,
  PracticeTechnique,
  TechniqueVisualCue
} from "./types";
import { createId, nowIso } from "./utils/ids";
import { searchLanguageFormulas, searchPracticeModules } from "./utils/manualEngine";

type View = "console" | "library" | "settings";
type LoadingState = "language" | "practice" | null;

const initialQuery: ManualQuery = {
  domain: "language-pattern",
  topic: "",
  keywords: "",
  situation: "",
  goal: "",
  tone: "차분하고 명료하게"
};

function domainLabel(domain: ManualDomain): string {
  return domain === "language-pattern" ? "언어 패턴" : "초능력/기공";
}

function splitList(value: string): string[] {
  return value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function listText(values: string[]): string {
  return values.join("\n");
}

function moduleCount(module: ManualModule): string {
  return module.domain === "language-pattern" ? module.formulas.length + "개 공식" : module.practices.length + "개 훈련";
}

function copyText(value: string): void {
  navigator.clipboard?.writeText(value).catch(() => undefined);
}

function generationLabel(result: { generationMode?: string; aiGenerated?: boolean; modelUsed?: string }): string {
  if (result.aiGenerated || result.generationMode === "gemini") {
    return "Gemini 생성" + (result.modelUsed ? " / " + result.modelUsed : "");
  }
  if (result.generationMode === "api-fallback") return "API 실패 - 로컬 폴백";
  return "로컬 폴백";
}

function GenerationBadge({ result }: { result: { generationMode?: string; aiGenerated?: boolean; modelUsed?: string } }) {
  const mode = result.aiGenerated || result.generationMode === "gemini" ? "ai" : "fallback";
  return <span className={"generation-badge " + mode}>{generationLabel(result)}</span>;
}

function App() {
  const [view, setView] = useState<View>("console");
  const [modules, setModules] = useState<ManualModule[]>(() => loadManualModules());
  const [query, setQuery] = useState<ManualQuery>(initialQuery);
  const [languageResult, setLanguageResult] = useState<LanguageRecommendation | null>(null);
  const [practiceMatches, setPracticeMatches] = useState<PracticeMatch[]>([]);
  const [practiceManual, setPracticeManual] = useState<PracticeManual | null>(null);
  const [loading, setLoading] = useState<LoadingState>(null);

  useEffect(() => {
    saveManualModules(modules);
  }, [modules]);

  const liveCandidates = useMemo(() => {
    return query.domain === "language-pattern"
      ? searchLanguageFormulas(modules, query, 5)
      : searchPracticeModules(modules, query, 5);
  }, [modules, query]);

  function updateQuery<K extends keyof ManualQuery>(field: K, value: ManualQuery[K]) {
    setQuery((current) => ({ ...current, [field]: value }));
  }

  function switchDomain(domain: ManualDomain) {
    setQuery((current) => ({ ...current, domain }));
    setLanguageResult(null);
    setPracticeMatches([]);
    setPracticeManual(null);
  }

  async function runConsole() {
    if (query.domain === "language-pattern") {
      setLoading("language");
      const result = await recommendLanguage(query, modules);
      setLanguageResult(result);
      setLoading(null);
      return;
    }

    const matches = recommendPracticeModules(query, modules);
    setPracticeMatches(matches);
    setPracticeManual(null);
  }

  async function buildPracticeManual(moduleId: string) {
    const module = modules.find((item): item is PracticeManualModule => item.id === moduleId && item.domain === "psi-qigong");
    if (!module) return;
    setLoading("practice");
    const result = await generatePracticeManual({ ...query, selectedModuleId: module.id }, module, modules);
    setPracticeManual(result);
    setLoading(null);
  }

  function upsertModule(nextModule: ManualModule) {
    setModules((current) => {
      const previous = current.find((module) => module.id === nextModule.id);
      const updated = {
        ...nextModule,
        updatedAt: nowIso(),
        revisions: previous ? [createManualRevision(previous), ...previous.revisions].slice(0, 20) : nextModule.revisions
      } as ManualModule;
      const next = previous
        ? current.map((module) => (module.id === updated.id ? updated : module))
        : [...current, updated];
      return next.sort((a, b) => a.sortOrder - b.sortOrder);
    });
  }

  function toggleModule(moduleId: string) {
    setModules((current) =>
      current.map((module) =>
        module.id === moduleId ? ({ ...module, active: !module.active, updatedAt: nowIso() } as ManualModule) : module
      )
    );
  }

  function deleteModule(moduleId: string) {
    if (!window.confirm("이 모듈을 삭제할까요?")) return;
    setModules((current) => current.filter((module) => module.id !== moduleId));
  }

  function duplicateModule(module: ManualModule) {
    setModules((current) => [...current, duplicateManualModule(module)].sort((a, b) => a.sortOrder - b.sortOrder));
  }

  function restoreRevision(moduleId: string, revisionId: string) {
    setModules((current) =>
      current.map((module) => {
        if (module.id !== moduleId) return module;
        const revision = module.revisions.find((item) => item.id === revisionId);
        return revision ? restoreManualSnapshot(module, revision.snapshot) : module;
      })
    );
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand-lockup" type="button" onClick={() => setView("console")} aria-label="홈 화면으로 이동">
          <div className="brand-mark">
            <Sparkles size={20} aria-hidden="true" />
          </div>
          <div>
            <p>Psychic Master</p>
            <h1>Manual Console</h1>
          </div>
        </button>
        <nav className="app-nav" aria-label="주요 화면">
          <button className={view === "console" ? "active" : ""} onClick={() => setView("console")}>
            <Search size={17} aria-hidden="true" />
            콘솔
          </button>
          <button className={view === "library" ? "active" : ""} onClick={() => setView("library")}>
            <Layers3 size={17} aria-hidden="true" />
            모듈
          </button>
          <button className={view === "settings" ? "active" : ""} onClick={() => setView("settings")}>
            <Settings size={17} aria-hidden="true" />
            설정
          </button>
        </nav>
      </header>

      <main>
        {view === "console" && (
          <ConsoleView
            query={query}
            updateQuery={updateQuery}
            switchDomain={switchDomain}
            liveCandidates={liveCandidates}
            loading={loading}
            runConsole={runConsole}
            languageResult={languageResult}
            practiceMatches={practiceMatches}
            practiceManual={practiceManual}
            buildPracticeManual={buildPracticeManual}
          />
        )}
        {view === "library" && (
          <LibraryView
            modules={modules}
            upsertModule={upsertModule}
            toggleModule={toggleModule}
            deleteModule={deleteModule}
            duplicateModule={duplicateModule}
            restoreRevision={restoreRevision}
          />
        )}
        {view === "settings" && <SettingsView modules={modules} />}
      </main>
    </div>
  );
}

function ConsoleView({
  query,
  updateQuery,
  switchDomain,
  liveCandidates,
  loading,
  runConsole,
  languageResult,
  practiceMatches,
  practiceManual,
  buildPracticeManual
}: {
  query: ManualQuery;
  updateQuery: <K extends keyof ManualQuery>(field: K, value: ManualQuery[K]) => void;
  switchDomain: (domain: ManualDomain) => void;
  liveCandidates: Array<{ moduleTitle: string; formula?: LanguagePatternFormula; practice?: PracticeTechnique; reason: string }>;
  loading: LoadingState;
  runConsole: () => void;
  languageResult: LanguageRecommendation | null;
  practiceMatches: PracticeMatch[];
  practiceManual: PracticeManual | null;
  buildPracticeManual: (moduleId: string) => void;
}) {
  return (
    <section className="console-grid">
      <div className="console-main">
        <div className="mode-strip" role="group" aria-label="매뉴얼 도메인">
          <button className={query.domain === "language-pattern" ? "active" : ""} onClick={() => switchDomain("language-pattern")}>
            <BookOpen size={18} aria-hidden="true" />
            언어 패턴
          </button>
          <button className={query.domain === "psi-qigong" ? "active" : ""} onClick={() => switchDomain("psi-qigong")}>
            <Brain size={18} aria-hidden="true" />
            초능력/기공
          </button>
        </div>

        <div className="input-panel">
          <div className="panel-heading">
            <div>
              <p>{domainLabel(query.domain)}</p>
              <h2>{query.domain === "language-pattern" ? "공식에 맞는 문장 추천" : "상황에 맞는 훈련 모듈 추천"}</h2>
            </div>
            <button className="primary-button" onClick={runConsole} disabled={loading !== null}>
              {loading ? <RefreshCw className="spin" size={18} aria-hidden="true" /> : <Wand2 size={18} aria-hidden="true" />}
              {query.domain === "language-pattern" ? "문장 추천" : "모듈 추천"}
            </button>
          </div>

          <div className="form-grid">
            <label>
              주제
              <input
                value={query.topic}
                onChange={(event) => updateQuery("topic", event.target.value)}
                placeholder={query.domain === "language-pattern" ? "예: 첫 만남, 설득, 거절" : "예: 단전 집중, 직관 선택, 기감"}
              />
            </label>
            <label>
              키워드
              <input
                value={query.keywords}
                onChange={(event) => updateQuery("keywords", event.target.value)}
                placeholder="쉼표로 구분"
              />
            </label>
          </div>

          <label>
            상황
            <textarea
              value={query.situation}
              onChange={(event) => updateQuery("situation", event.target.value)}
              placeholder={query.domain === "language-pattern" ? "상대 발화나 대화 맥락을 붙여넣으세요." : "훈련하고 싶은 상태, 장소, 제약을 적으세요."}
            />
          </label>

          <div className="form-grid">
            <label>
              목표
              <input
                value={query.goal}
                onChange={(event) => updateQuery("goal", event.target.value)}
                placeholder={query.domain === "language-pattern" ? "예: 부담 없이 확인 질문으로 전환" : "예: 10분 안에 감각 기록"}
              />
            </label>
            <label>
              톤
              <input value={query.tone} onChange={(event) => updateQuery("tone", event.target.value)} />
            </label>
          </div>
        </div>

        {query.domain === "language-pattern" && languageResult && <LanguageResult result={languageResult} />}
        {query.domain === "psi-qigong" && practiceMatches.length > 0 && (
          <PracticeResults matches={practiceMatches} loading={loading === "practice"} buildPracticeManual={buildPracticeManual} />
        )}
        {query.domain === "psi-qigong" && practiceManual && <PracticeManualView manual={practiceManual} />}
      </div>

      <aside className="candidate-rail">
        <div className="rail-header">
          <Eye size={18} aria-hidden="true" />
          실시간 후보
        </div>
        {liveCandidates.length === 0 && <p className="empty-text">활성 모듈이 없습니다.</p>}
        {liveCandidates.map((item, index) => (
          <div className="candidate-card" key={(item.formula?.id || item.practice?.id || item.moduleTitle) + index}>
            <span>{item.moduleTitle}</span>
            <strong>{item.formula?.name || item.practice?.name}</strong>
            <small>{item.reason}</small>
          </div>
        ))}
      </aside>
    </section>
  );
}

function LanguageResult({ result }: { result: LanguageRecommendation }) {
  return (
    <section className="result-stack">
      <div className="result-panel highlight">
        <div className="panel-heading compact-heading">
          <div>
            <p>지금 쓸 문장</p>
            <h2>{result.summary}</h2>
            <GenerationBadge result={result} />
          </div>
          <button className="icon-action" onClick={() => copyText(result.suggestedLines.join("\n"))} title="문장 복사">
            <Copy size={18} aria-hidden="true" />
          </button>
        </div>
        <div className="line-list">
          {result.suggestedLines.map((line, index) => (
            <article key={line + index} className="speech-line">
              <span>{index + 1}</span>
              <p>{line}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="result-grid">
        {result.variations.map((variation) => (
          <article className="result-panel" key={variation.label}>
            <div className="mini-heading">{variation.label}</div>
            <p>{variation.line}</p>
            <button className="text-button" onClick={() => copyText(variation.line)}>
              <Copy size={16} aria-hidden="true" />
              복사
            </button>
          </article>
        ))}
      </div>

      <details className="detail-panel" open>
        <summary>사용된 공식</summary>
        <div className="formula-stack">
          {result.selectedFormulas.map((formula) => (
            <article className="formula-card" key={formula.moduleTitle + formula.formulaName}>
              <span>{formula.moduleTitle} / {formula.formulaGroup}</span>
              <h3>{formula.formulaName}</h3>
              <dl>
                <dt>매뉴얼 공식</dt>
                <dd>{formula.manualFormula}</dd>
                <dt>실전 전환</dt>
                <dd>{formula.practiceFormula}</dd>
              </dl>
            </article>
          ))}
        </div>
      </details>

      <div className="result-grid">
        <ListPanel title="주의점" items={result.cautions} />
        <ListPanel title="다음 행동" items={result.nextSteps} />
      </div>
    </section>
  );
}

function PracticeResults({
  matches,
  loading,
  buildPracticeManual
}: {
  matches: PracticeMatch[];
  loading: boolean;
  buildPracticeManual: (moduleId: string) => void;
}) {
  const uniqueModules = Array.from(new Map(matches.map((match) => [match.moduleId, match])).values());

  return (
    <section className="result-stack">
      <div className="section-title">
        <h2>추천 모듈</h2>
        <span>{uniqueModules.length}개</span>
      </div>
      <div className="module-result-grid">
        {uniqueModules.map((match) => (
          <article className="module-card" key={match.moduleId}>
            <VisualFrame cue={match.practice.visualCue} title={match.practice.name} />
            <div className="module-card-body">
              <span>{match.moduleCategory}</span>
              <h3>{match.moduleTitle}</h3>
              <p>{match.practice.name}</p>
              <small>{match.reason}</small>
              <button className="primary-button full" onClick={() => buildPracticeManual(match.moduleId)} disabled={loading}>
                {loading ? <RefreshCw className="spin" size={17} aria-hidden="true" /> : <Wand2 size={17} aria-hidden="true" />}
                맞춤 매뉴얼 생성
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function PracticeManualView({ manual }: { manual: PracticeManual }) {
  return (
    <section className="result-stack">
      <div className="result-panel highlight practice-manual">
        <VisualFrame cue={manual.visualCue} title={manual.title} />
        <div>
          <p className="eyebrow-text">{manual.selectedModuleTitle}</p>
          <h2>{manual.title}</h2>
          <p>{manual.summary}</p>
          <GenerationBadge result={manual} />
          <div className="meta-row">
            <span>{manual.duration}</span>
            <span>주관적 훈련 기록</span>
          </div>
        </div>
      </div>
      <div className="result-grid">
        <ListPanel title="준비" items={manual.preparation} />
        <ListPanel title="맞춤 조정" items={manual.adaptations} />
      </div>
      <div className="result-panel">
        <div className="mini-heading">실행 단계</div>
        <ol className="step-list">
          {manual.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>
      <div className="result-grid">
        <ListPanel title="기록 질문" items={manual.logPrompts} />
        <ListPanel title="안전 기준" items={manual.safety} />
      </div>
    </section>
  );
}

function ListPanel({ title, items }: { title: string; items: string[] }) {
  return (
    <article className="result-panel">
      <div className="mini-heading">{title}</div>
      <ul className="clean-list">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </article>
  );
}

function LibraryView({
  modules,
  upsertModule,
  toggleModule,
  deleteModule,
  duplicateModule,
  restoreRevision
}: {
  modules: ManualModule[];
  upsertModule: (module: ManualModule) => void;
  toggleModule: (moduleId: string) => void;
  deleteModule: (moduleId: string) => void;
  duplicateModule: (module: ManualModule) => void;
  restoreRevision: (moduleId: string, revisionId: string) => void;
}) {
  const [filter, setFilter] = useState<"all" | ManualDomain>("all");
  const [draft, setDraft] = useState<ManualModule | null>(null);
  const filtered = modules.filter((module) => filter === "all" || module.domain === filter);

  function createModule(domain: ManualDomain) {
    setDraft(createBlankManualModule(domain));
  }

  function saveDraft() {
    if (!draft) return;
    upsertModule(draft);
    setDraft(null);
  }

  return (
    <section className="library-layout">
      <div className="library-list">
        <div className="toolbar">
          <div>
            <p>Module Library</p>
            <h2>매뉴얼 모듈</h2>
          </div>
          <div className="toolbar-actions">
            <button onClick={() => createModule("language-pattern")}>
              <Plus size={16} aria-hidden="true" />
              언어
            </button>
            <button onClick={() => createModule("psi-qigong")}>
              <Plus size={16} aria-hidden="true" />
              기공
            </button>
          </div>
        </div>

        <div className="mode-strip small">
          <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>
            전체
          </button>
          <button className={filter === "language-pattern" ? "active" : ""} onClick={() => setFilter("language-pattern")}>
            언어 패턴
          </button>
          <button className={filter === "psi-qigong" ? "active" : ""} onClick={() => setFilter("psi-qigong")}>
            초능력/기공
          </button>
        </div>

        <div className="module-list">
          {filtered.map((module) => (
            <article className={"module-row " + (!module.active ? "disabled" : "")} key={module.id}>
              <VisualFrame cue={module.visualCue} title={module.title} compact />
              <div>
                <span>{domainLabel(module.domain)} / {module.category}</span>
                <h3>{module.title}</h3>
                <p>{module.description}</p>
                <div className="meta-row">
                  <span>{moduleCount(module)}</span>
                  <span>{module.sourceLabel}</span>
                </div>
              </div>
              <div className="row-actions">
                <button onClick={() => setDraft(module)} title="수정">
                  <Edit3 size={16} aria-hidden="true" />
                </button>
                <button onClick={() => duplicateModule(module)} title="복제">
                  <Copy size={16} aria-hidden="true" />
                </button>
                <button onClick={() => toggleModule(module.id)} title="활성 전환">
                  <Eye size={16} aria-hidden="true" />
                </button>
                <button className="danger" onClick={() => deleteModule(module.id)} title="삭제">
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      <aside className="editor-panel">
        {draft ? (
          <ModuleEditor module={draft} onChange={setDraft} onSave={saveDraft} onCancel={() => setDraft(null)} restoreRevision={restoreRevision} />
        ) : (
          <div className="empty-editor">
            <Layers3 size={24} aria-hidden="true" />
            <h2>모듈을 선택하세요</h2>
            <p>공식과 훈련은 모듈 안에서 그룹으로 관리됩니다.</p>
          </div>
        )}
      </aside>
    </section>
  );
}

function ModuleEditor({
  module,
  onChange,
  onSave,
  onCancel,
  restoreRevision
}: {
  module: ManualModule;
  onChange: (module: ManualModule) => void;
  onSave: () => void;
  onCancel: () => void;
  restoreRevision: (moduleId: string, revisionId: string) => void;
}) {
  function updateCore(patch: Partial<ManualModule>) {
    onChange({ ...module, ...patch } as ManualModule);
  }

  return (
    <div className="editor-stack">
      <div className="panel-heading compact-heading">
        <div>
          <p>{domainLabel(module.domain)}</p>
          <h2>{module.title}</h2>
        </div>
        <div className="toolbar-actions">
          <button onClick={onCancel}>취소</button>
          <button className="primary-button" onClick={onSave}>
            <Save size={16} aria-hidden="true" />
            저장
          </button>
        </div>
      </div>

      <div className="edit-form">
        <label>
          모듈명
          <input value={module.title} onChange={(event) => updateCore({ title: event.target.value })} />
        </label>
        <label>
          설명
          <textarea value={module.description} onChange={(event) => updateCore({ description: event.target.value })} />
        </label>
        <div className="form-grid">
          <label>
            카테고리
            <input value={module.category} onChange={(event) => updateCore({ category: event.target.value })} />
          </label>
          <label>
            출처
            <input value={module.sourceLabel} onChange={(event) => updateCore({ sourceLabel: event.target.value })} />
          </label>
        </div>
        <div className="form-grid">
          <label>
            태그
            <textarea value={module.tags.join(", ")} onChange={(event) => updateCore({ tags: splitList(event.target.value) })} />
          </label>
          <label>
            검색 키워드
            <textarea value={module.keywords.join(", ")} onChange={(event) => updateCore({ keywords: splitList(event.target.value) })} />
          </label>
        </div>
      </div>

      {module.domain === "language-pattern" ? (
        <LanguageFormulaManager module={module} onChange={onChange} />
      ) : (
        <PracticeTechniqueManager module={module} onChange={onChange} />
      )}

      {module.revisions.length > 0 && (
        <details className="detail-panel">
          <summary>버전 복원</summary>
          <div className="revision-list">
            {module.revisions.map((revision) => (
              <button key={revision.id} onClick={() => restoreRevision(module.id, revision.id)}>
                <RefreshCw size={15} aria-hidden="true" />
                {new Date(revision.savedAt).toLocaleString()} / {revision.note}
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function blankFormula(): LanguagePatternFormula {
  return {
    id: createId("formula"),
    group: "새 묶음",
    name: "새 공식",
    manualFormula: "매뉴얼 공식을 입력하세요.",
    practiceFormula: "실전 문장으로 전환하세요.",
    principle: "핵심 원리를 입력하세요.",
    triggerSituations: [],
    examples: [],
    cautions: [],
    tags: []
  };
}

function LanguageFormulaManager({ module, onChange }: { module: LanguageManualModule; onChange: (module: ManualModule) => void }) {
  const [selectedId, setSelectedId] = useState(module.formulas[0]?.id ?? "");
  const selected = module.formulas.find((formula) => formula.id === selectedId) ?? module.formulas[0];

  useEffect(() => {
    if (!selected && module.formulas[0]) setSelectedId(module.formulas[0].id);
  }, [module.formulas, selected]);

  function updateFormula(patch: Partial<LanguagePatternFormula>) {
    if (!selected) return;
    onChange({
      ...module,
      formulas: module.formulas.map((formula) => (formula.id === selected.id ? { ...formula, ...patch } : formula))
    });
  }

  function addFormula() {
    const next = blankFormula();
    onChange({ ...module, formulas: [...module.formulas, next] });
    setSelectedId(next.id);
  }

  function deleteFormula() {
    if (!selected) return;
    const next = module.formulas.filter((formula) => formula.id !== selected.id);
    onChange({ ...module, formulas: next });
    setSelectedId(next[0]?.id ?? "");
  }

  return (
    <div className="editor-section">
      <div className="section-title">
        <h3>공식 카드</h3>
        <button onClick={addFormula}>
          <Plus size={16} aria-hidden="true" />
          공식 추가
        </button>
      </div>
      <div className="formula-picker">
        {module.formulas.map((formula) => (
          <button className={formula.id === selected?.id ? "active" : ""} key={formula.id} onClick={() => setSelectedId(formula.id)}>
            <span>{formula.group}</span>
            {formula.name}
          </button>
        ))}
      </div>
      {selected && (
        <div className="edit-form">
          <div className="form-grid">
            <label>
              그룹
              <input value={selected.group} onChange={(event) => updateFormula({ group: event.target.value })} />
            </label>
            <label>
              공식명
              <input value={selected.name} onChange={(event) => updateFormula({ name: event.target.value })} />
            </label>
          </div>
          <label>
            매뉴얼 공식
            <textarea value={selected.manualFormula} onChange={(event) => updateFormula({ manualFormula: event.target.value })} />
          </label>
          <label>
            실전 문장 공식
            <textarea value={selected.practiceFormula} onChange={(event) => updateFormula({ practiceFormula: event.target.value })} />
          </label>
          <label>
            원리
            <textarea value={selected.principle} onChange={(event) => updateFormula({ principle: event.target.value })} />
          </label>
          <div className="form-grid">
            <label>
              사용 상황
              <textarea value={listText(selected.triggerSituations)} onChange={(event) => updateFormula({ triggerSituations: splitList(event.target.value) })} />
            </label>
            <label>
              주의점
              <textarea value={listText(selected.cautions)} onChange={(event) => updateFormula({ cautions: splitList(event.target.value) })} />
            </label>
          </div>
          <div className="toolbar-actions">
            <button className="danger" onClick={deleteFormula}>
              <Trash2 size={16} aria-hidden="true" />
              공식 삭제
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function blankPractice(): PracticeTechnique {
  return {
    id: createId("practice"),
    group: "새 묶음",
    name: "새 훈련",
    intent: "훈련 목적을 입력하세요.",
    recommendedFor: [],
    keywords: [],
    setup: [],
    steps: [],
    duration: "5-10분",
    difficulty: "입문",
    safety: ["불편감이 생기면 즉시 멈춥니다."],
    customizationPrompts: [],
    successCriteria: [],
    cautions: [],
    tags: []
  };
}

function PracticeTechniqueManager({ module, onChange }: { module: PracticeManualModule; onChange: (module: ManualModule) => void }) {
  const [selectedId, setSelectedId] = useState(module.practices[0]?.id ?? "");
  const selected = module.practices.find((practice) => practice.id === selectedId) ?? module.practices[0];

  useEffect(() => {
    if (!selected && module.practices[0]) setSelectedId(module.practices[0].id);
  }, [module.practices, selected]);

  function updatePractice(patch: Partial<PracticeTechnique>) {
    if (!selected) return;
    onChange({
      ...module,
      practices: module.practices.map((practice) => (practice.id === selected.id ? { ...practice, ...patch } : practice))
    });
  }

  function addPractice() {
    const next = blankPractice();
    onChange({ ...module, practices: [...module.practices, next] });
    setSelectedId(next.id);
  }

  function deletePractice() {
    if (!selected) return;
    const next = module.practices.filter((practice) => practice.id !== selected.id);
    onChange({ ...module, practices: next });
    setSelectedId(next[0]?.id ?? "");
  }

  return (
    <div className="editor-section">
      <div className="section-title">
        <h3>훈련 테크닉</h3>
        <button onClick={addPractice}>
          <Plus size={16} aria-hidden="true" />
          훈련 추가
        </button>
      </div>
      <div className="formula-picker">
        {module.practices.map((practice) => (
          <button className={practice.id === selected?.id ? "active" : ""} key={practice.id} onClick={() => setSelectedId(practice.id)}>
            <span>{practice.group}</span>
            {practice.name}
          </button>
        ))}
      </div>
      {selected && (
        <div className="edit-form">
          <div className="form-grid">
            <label>
              그룹
              <input value={selected.group} onChange={(event) => updatePractice({ group: event.target.value })} />
            </label>
            <label>
              훈련명
              <input value={selected.name} onChange={(event) => updatePractice({ name: event.target.value })} />
            </label>
          </div>
          <label>
            목적
            <textarea value={selected.intent} onChange={(event) => updatePractice({ intent: event.target.value })} />
          </label>
          <div className="form-grid">
            <label>
              준비
              <textarea value={listText(selected.setup)} onChange={(event) => updatePractice({ setup: splitList(event.target.value) })} />
            </label>
            <label>
              실행 단계
              <textarea value={listText(selected.steps)} onChange={(event) => updatePractice({ steps: splitList(event.target.value) })} />
            </label>
          </div>
          <div className="form-grid">
            <label>
              권장 상황
              <textarea value={listText(selected.recommendedFor)} onChange={(event) => updatePractice({ recommendedFor: splitList(event.target.value) })} />
            </label>
            <label>
              안전 기준
              <textarea value={listText(selected.safety)} onChange={(event) => updatePractice({ safety: splitList(event.target.value) })} />
            </label>
          </div>
          <div className="form-grid">
            <label>
              시간
              <input value={selected.duration} onChange={(event) => updatePractice({ duration: event.target.value })} />
            </label>
            <label>
              난이도
              <select value={selected.difficulty} onChange={(event) => updatePractice({ difficulty: event.target.value as PracticeTechnique["difficulty"] })}>
                <option>입문</option>
                <option>중급</option>
                <option>상급</option>
              </select>
            </label>
          </div>
          <div className="toolbar-actions">
            <button className="danger" onClick={deletePractice}>
              <Trash2 size={16} aria-hidden="true" />
              훈련 삭제
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsView({ modules }: { modules: ManualModule[] }) {
  const languageCount = modules.filter((module) => module.domain === "language-pattern").length;
  const practiceCount = modules.filter((module) => module.domain === "psi-qigong").length;
  const [geminiStatus, setGeminiStatus] = useState<GeminiStatus | null>(null);

  useEffect(() => {
    let mounted = true;
    getGeminiStatus().then((status) => {
      if (mounted) setGeminiStatus(status);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className="settings-grid">
      <article className="result-panel">
        <div className="mini-heading">저장된 모듈</div>
        <div className="stats-grid">
          <div>
            <strong>{languageCount}</strong>
            <span>언어 패턴</span>
          </div>
          <div>
            <strong>{practiceCount}</strong>
            <span>초능력/기공</span>
          </div>
        </div>
      </article>
      <article className="result-panel">
        <div className="mini-heading">Google API</div>
        <div className="api-status-row">
          <span className={geminiStatus?.configured ? "generation-badge ai" : "generation-badge fallback"}>
            {geminiStatus?.configured ? "Gemini 연결됨" : "GEMINI_API_KEY 미등록"}
          </span>
          <strong>{geminiStatus?.model || "확인 중"}</strong>
        </div>
        <ul className="clean-list">
          <li>Cloudflare Pages 환경변수: GEMINI_API_KEY</li>
          <li>선택 환경변수: GEMINI_MODEL</li>
          <li>클라이언트에는 API 키를 노출하지 않습니다.</li>
        </ul>
      </article>
      <article className="result-panel">
        <div className="mini-heading">추천 URL</div>
        <p>https://psychic-master.pages.dev</p>
      </article>
    </section>
  );
}

function VisualFrame({ cue, title, compact = false }: { cue?: TechniqueVisualCue; title: string; compact?: boolean }) {
  return (
    <figure className={compact ? "visual-frame compact" : "visual-frame"}>
      {cue?.imageUrl ? <img src={cue.imageUrl} alt={cue.imageAlt || title} /> : <div className="visual-placeholder">{title.slice(0, 2)}</div>}
      {!compact && cue?.quickInstruction && <figcaption>{cue.quickInstruction}</figcaption>}
    </figure>
  );
}

export default App;
